import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, Alert, CircularProgress, IconButton, Chip
} from '@mui/material';
import { Home, Print, Balance, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const todayStr = () => new Date().toISOString().split('T')[0];

const TrialBalance = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/reports/trial-balance`, { headers: hdrs(), params: { from, to } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load trial balance'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      [company?.name || 'TRIAL BALANCE'],
      ['TRIAL BALANCE'],
      [`${from ? `${from} to ` : 'As of '}${to}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Account', 'Name', 'Debit', 'Credit'],
      ...data.rows.map(r => [r.accountNo, r.accountName, r.debit || '', r.credit || '']),
      [],
      ['', 'TOTAL', data.totals.debit, data.totals.credit],
      ['', 'Status', data.balanced ? 'Balanced' : 'Out of balance'],
    ];
    downloadCsv(rows, `trial_balance_${to}.csv`);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .tb-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="From (optional)" value={from} size="small"
          onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="As of" value={to} size="small"
          onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#1565c015' }}>
          {loading ? <CircularProgress size={20} /> : <Balance />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="tb-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="TRIAL BALANCE" subtitle={`${from ? from + ' → ' : 'As of '}${to}`} />

          {data && (
            <>
              {!data.balanced && (
                <Alert severity="warning" sx={{ mb: 2 }} className="no-print">
                  Out of balance by {sym()} {fmt(Math.abs(data.totals.debit - data.totals.credit))} — check GL postings.
                </Alert>
              )}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Account</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell align="right"><strong>Debit</strong></TableCell>
                      <TableCell align="right"><strong>Credit</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map(r => (
                      <TableRow key={r.accountNo} hover>
                        <TableCell><strong>{r.accountNo}</strong></TableCell>
                        <TableCell>{r.accountName}</TableCell>
                        <TableCell align="right">{r.debit ? fmt(r.debit) : ''}</TableCell>
                        <TableCell align="right">{r.credit ? fmt(r.credit) : ''}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 800 }}>
                        TOTAL {data.balanced
                          ? <Chip label="Balanced" size="small" color="success" sx={{ ml: 1 }} />
                          : <Chip label="Out of balance" size="small" color="error" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{sym()} {fmt(data.totals.debit)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{sym()} {fmt(data.totals.credit)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TrialBalance;
